import React, { useState, useEffect, useMemo } from 'react';
import {
  BookOpen, Users, UserPlus, Clock, CheckCircle2, AlertTriangle,
  Search, Plus, Trash2, RefreshCw, ChevronDown, X, CalendarDays,
  BarChart3, Shield, Target, Loader2, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

const STATUS_MAP = {
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Target },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

export default function TrainingAssignmentAdmin() {
  const [tab, setTab] = useState('assignments');
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState({});
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Assign dialog
  const [showAssign, setShowAssign] = useState(false);
  const [assignMode, setAssignMode] = useState('users'); // 'users' or 'groups'
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [mandatory, setMandatory] = useState(true);
  const [assigning, setAssigning] = useState(false);

  // Group dialog
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [savingGroup, setSavingGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [aRes, mRes, uRes, gRes] = await Promise.all([
        api.get('/training/admin/assignments').catch(() => ({ data: { items: [], stats: {} } })),
        api.get('/training/modules').catch(() => ({ data: { modules: [] } })),
        api.get('/training/admin/tenant-users').catch(() => ({ data: { users: [] } })),
        api.get('/training/admin/groups').catch(() => ({ data: { groups: [] } })),
      ]);
      setAssignments(aRes.data.items || []);
      setStats(aRes.data.stats || {});
      setModules(mRes.data.modules || []);
      setUsers(uRes.data.users || []);
      setGroups(gRes.data.groups || []);
    } catch (err) {
      toast.error('Failed to load training data');
    } finally { setLoading(false); }
  };

  const filteredAssignments = useMemo(() => {
    let items = assignments;
    if (statusFilter !== 'all') items = items.filter(a => a.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(a =>
        (a.user_name || '').toLowerCase().includes(q) ||
        (a.user_email || '').toLowerCase().includes(q) ||
        (a.module_title || '').toLowerCase().includes(q) ||
        (a.module_code || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [assignments, statusFilter, search]);

  const handleAssign = async () => {
    if (!selectedModules.length) return toast.error('Select at least one training module');
    if (assignMode === 'users' && !selectedUsers.length) return toast.error('Select at least one user');
    if (assignMode === 'groups' && !selectedGroups.length) return toast.error('Select at least one group');

    setAssigning(true);
    try {
      const payload = {
        module_codes: selectedModules,
        ...(assignMode === 'users' ? { user_ids: selectedUsers } : { group_ids: selectedGroups }),
        mandatory,
      };
      if (dueDate) payload.due_date = dueDate;
      const res = await api.post('/training/admin/assign', payload);
      toast.success(`${res.data.assignments_created} assignments created, ${res.data.assignments_skipped} updated`);
      setShowAssign(false);
      resetAssignForm();
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign training');
    } finally { setAssigning(false); }
  };

  const resetAssignForm = () => {
    setSelectedModules([]); setSelectedUsers([]); setSelectedGroups([]);
    setDueDate(''); setMandatory(true); setAssignMode('users');
  };

  const handleDeleteAssignment = async (id) => {
    try {
      await api.delete(`/training/admin/assignments/${id}`);
      toast.success('Assignment removed');
      setAssignments(prev => prev.filter(a => a.id !== id));
    } catch { toast.error('Failed to remove assignment'); }
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) return toast.error('Group name is required');
    setSavingGroup(true);
    try {
      if (editingGroup) {
        await api.put(`/training/admin/groups/${editingGroup.id}`, {
          name: groupName, description: groupDesc, member_ids: groupMembers,
        });
        toast.success('Group updated');
      } else {
        await api.post('/training/admin/groups', {
          name: groupName, description: groupDesc, member_ids: groupMembers,
        });
        toast.success('Group created');
      }
      setShowGroupDialog(false);
      resetGroupForm();
      loadAll();
    } catch { toast.error('Failed to save group'); }
    finally { setSavingGroup(false); }
  };

  const resetGroupForm = () => {
    setGroupName(''); setGroupDesc(''); setGroupMembers([]); setEditingGroup(null);
  };

  const handleEditGroup = (g) => {
    setEditingGroup(g); setGroupName(g.name); setGroupDesc(g.description || '');
    setGroupMembers(g.member_ids || []); setShowGroupDialog(true);
  };

  const handleDeleteGroup = async (id) => {
    try {
      await api.delete(`/training/admin/groups/${id}`);
      toast.success('Group deleted');
      setGroups(prev => prev.filter(g => g.id !== id));
    } catch { toast.error('Failed to delete group'); }
  };

  const toggleItem = (list, setList, id) => {
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="training-admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="training-assignment-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Training Administration</h1>
          <p className="text-sm text-muted-foreground mt-1">Assign training modules to users and manage groups</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadAll} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowAssign(true)} data-testid="assign-training-btn">
            <Send className="h-4 w-4 mr-1" /> Assign Training
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, icon: BookOpen, color: 'text-blue-600' },
          { label: 'Assigned', value: stats.assigned || 0, icon: Clock, color: 'text-slate-600' },
          { label: 'In Progress', value: stats.in_progress || 0, icon: Target, color: 'text-amber-600' },
          { label: 'Completed', value: stats.completed || 0, icon: CheckCircle2, color: 'text-emerald-600' },
          { label: 'Overdue', value: stats.overdue || 0, icon: AlertTriangle, color: 'text-red-600' },
        ].map(s => (
          <Card key={s.label} data-testid={`stat-${s.label.toLowerCase()}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className="text-xl font-bold">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="assignments" data-testid="tab-assignments">Assignments</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">User Groups</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Completion Reports</TabsTrigger>
        </TabsList>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="mt-4 space-y-4">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, module..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="assignment-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredAssignments.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>No assignments found. Click "Assign Training" to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">User</th>
                    <th className="text-left p-3 font-medium">Module</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Progress</th>
                    <th className="text-left p-3 font-medium">Due Date</th>
                    <th className="text-left p-3 font-medium">Score</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssignments.map(a => {
                    const st = STATUS_MAP[a.status] || STATUS_MAP.assigned;
                    const isOverdue = a.due_date && new Date(a.due_date) < new Date() && a.status !== 'completed';
                    return (
                      <tr key={a.id} className="border-t hover:bg-muted/30" data-testid={`assignment-row-${a.id}`}>
                        <td className="p-3">
                          <div className="font-medium">{a.user_name || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground">{a.user_email}</div>
                        </td>
                        <td className="p-3">
                          <div>{a.module_title || a.module_code}</div>
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary" className={isOverdue ? 'bg-red-100 text-red-700' : st.color}>
                            {isOverdue ? 'Overdue' : st.label}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${a.progress_pct || 0}%` }} />
                            </div>
                            <span className="text-xs">{a.progress_pct || 0}%</span>
                          </div>
                        </td>
                        <td className="p-3 text-xs">
                          {a.due_date ? new Date(a.due_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3">
                          {a.quiz_score != null ? `${a.quiz_score}%` : '-'}
                        </td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteAssignment(a.id)} data-testid={`delete-assignment-${a.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Organize users into groups for bulk training assignments</p>
            <Button size="sm" onClick={() => { resetGroupForm(); setShowGroupDialog(true); }} data-testid="create-group-btn">
              <Plus className="h-4 w-4 mr-1" /> Create Group
            </Button>
          </div>
          {groups.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>No groups yet. Create a group to assign training in bulk.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {groups.map(g => (
                <Card key={g.id} data-testid={`group-card-${g.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{g.name}</CardTitle>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditGroup(g)} data-testid={`edit-group-${g.id}`}>
                          <UserPlus className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteGroup(g.id)} data-testid={`delete-group-${g.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {g.description && <p className="text-xs text-muted-foreground mb-2">{g.description}</p>}
                    <Badge variant="secondary">
                      <Users className="h-3 w-3 mr-1" /> {g.member_count || (g.member_ids || []).length} members
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-4">
          <CompletionReportsTab />
        </TabsContent>
      </Tabs>

      {/* Assign Training Dialog */}
      <Dialog open={showAssign} onOpenChange={v => { if (!v) { setShowAssign(false); resetAssignForm(); } else setShowAssign(true); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Training</DialogTitle>
            <DialogDescription>Select modules and users/groups to create assignments</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Step 1: Select Modules */}
            <div>
              <label className="text-sm font-medium mb-2 block">Training Modules</label>
              <ScrollArea className="h-40 border rounded-md p-2">
                {modules.map(m => (
                  <label key={m.code} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer" data-testid={`module-option-${m.code}`}>
                    <Checkbox
                      checked={selectedModules.includes(m.code)}
                      onCheckedChange={() => toggleItem(selectedModules, setSelectedModules, m.code)}
                    />
                    <span className="text-sm flex-1">{m.title}</span>
                    <Badge variant="outline" className="text-[10px]">{m.category}</Badge>
                  </label>
                ))}
              </ScrollArea>
              {selectedModules.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{selectedModules.length} module(s) selected</p>
              )}
            </div>

            {/* Step 2: Assign To */}
            <div>
              <label className="text-sm font-medium mb-2 block">Assign To</label>
              <Tabs value={assignMode} onValueChange={setAssignMode}>
                <TabsList className="mb-2">
                  <TabsTrigger value="users">Individual Users</TabsTrigger>
                  <TabsTrigger value="groups">Groups</TabsTrigger>
                </TabsList>

                <TabsContent value="users">
                  <ScrollArea className="h-40 border rounded-md p-2">
                    {users.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">No users found in tenant</p>
                    ) : users.map(u => (
                      <label key={u.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer" data-testid={`user-option-${u.user_id}`}>
                        <Checkbox
                          checked={selectedUsers.includes(u.user_id)}
                          onCheckedChange={() => toggleItem(selectedUsers, setSelectedUsers, u.user_id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{u.full_name || u.email}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>
                        </div>
                        <Badge variant="outline" className="text-[10px] shrink-0">{u.role}</Badge>
                      </label>
                    ))}
                  </ScrollArea>
                  {selectedUsers.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{selectedUsers.length} user(s) selected</p>
                  )}
                </TabsContent>

                <TabsContent value="groups">
                  <ScrollArea className="h-40 border rounded-md p-2">
                    {groups.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">No groups found. Create one in the Groups tab.</p>
                    ) : groups.map(g => (
                      <label key={g.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer" data-testid={`group-option-${g.id}`}>
                        <Checkbox
                          checked={selectedGroups.includes(g.id)}
                          onCheckedChange={() => toggleItem(selectedGroups, setSelectedGroups, g.id)}
                        />
                        <div className="flex-1">
                          <div className="text-sm">{g.name}</div>
                          <div className="text-[10px] text-muted-foreground">{g.member_count || 0} members</div>
                        </div>
                      </label>
                    ))}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>

            {/* Step 3: Options */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Due Date (optional)</label>
                <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="due-date-input" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={mandatory} onCheckedChange={setMandatory} data-testid="mandatory-checkbox" />
                  <span className="text-sm">Mandatory</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssign(false); resetAssignForm(); }}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assigning} data-testid="confirm-assign-btn">
              {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={v => { if (!v) { setShowGroupDialog(false); resetGroupForm(); } else setShowGroupDialog(true); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
            <DialogDescription>Organize users into groups for bulk assignments</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Group Name</label>
              <Input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="e.g., Engineering Team" data-testid="group-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea value={groupDesc} onChange={e => setGroupDesc(e.target.value)} placeholder="Optional description" rows={2} data-testid="group-desc-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Members</label>
              <ScrollArea className="h-48 border rounded-md p-2">
                {users.map(u => (
                  <label key={u.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer">
                    <Checkbox
                      checked={groupMembers.includes(u.user_id)}
                      onCheckedChange={() => toggleItem(groupMembers, setGroupMembers, u.user_id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{u.full_name || u.email}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>
                    </div>
                  </label>
                ))}
              </ScrollArea>
              <p className="text-xs text-muted-foreground mt-1">{groupMembers.length} member(s) selected</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGroupDialog(false); resetGroupForm(); }}>Cancel</Button>
            <Button onClick={handleSaveGroup} disabled={savingGroup} data-testid="save-group-btn">
              {savingGroup ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Completion Reports Sub-tab
function CompletionReportsTab() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/training/reports/completion')
      .then(res => setReport(res.data.report || []))
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="completion-reports">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left p-3 font-medium">Module</th>
            <th className="text-left p-3 font-medium">Category</th>
            <th className="text-center p-3 font-medium">Completed</th>
            <th className="text-center p-3 font-medium">In Progress</th>
            <th className="text-center p-3 font-medium">Not Started</th>
            <th className="text-center p-3 font-medium">Completion Rate</th>
            <th className="text-center p-3 font-medium">Avg Score</th>
          </tr>
        </thead>
        <tbody>
          {report.map(r => (
            <tr key={r.code} className="border-t hover:bg-muted/30">
              <td className="p-3 font-medium">{r.title}</td>
              <td className="p-3"><Badge variant="outline" className="text-[10px]">{r.category}</Badge></td>
              <td className="p-3 text-center text-emerald-600 font-medium">{r.completed}</td>
              <td className="p-3 text-center text-amber-600">{r.in_progress}</td>
              <td className="p-3 text-center text-slate-500">{r.not_started}</td>
              <td className="p-3 text-center">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${r.completion_rate}%` }} />
                  </div>
                  <span className="text-xs">{r.completion_rate}%</span>
                </div>
              </td>
              <td className="p-3 text-center">{r.avg_quiz_score}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
